import React, { useEffect } from 'react'
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import FooterContact from '../../../component/FooterContact';
import HomeVideo2 from '../../../assets/images/banner/banner-1.mp4';

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } }
};

const benefits = [
    { icon: '🔍', title: 'Improved Threat Intelligence', desc: 'Analyzes vast datasets in real time, offering predictive insights to foresee potential attacks and take proactive defensive steps.' },
    { icon: '⚡', title: 'Improved Response Speed', desc: 'Automates threat detection, analysis, and mitigation - reducing time from detection to action and minimizing breach impacts.' },
    { icon: '👁️', title: 'Continuous Monitoring', desc: 'Detects risks within networks and systems 24/7, prioritizing vulnerabilities based on criticality while minimizing false positives.' }
];

const capabilities = [
    { img: require('../../../assets/images/services/technology/ai-1.webp'), title: 'AI Identifies Unknown Threats', desc: 'AI is more effective at mapping and stopping unknown threats that human analysts might miss. It can detect novel attack vectors and zero-day exploits by pattern recognition and behavioral analysis.' },
    { img: require('../../../assets/images/services/technology/ai-2.webp'), title: 'AI Can Handle Massive Data', desc: 'A company generates enormous volumes of traffic data. AI can detect potential threats hidden within large data streams, providing cybersecurity teams with actionable intelligence at scale.' },
    { img: require('../../../assets/images/services/technology/ai-3.webp'), title: 'Better Vulnerability Management', desc: 'AI analyzes existing security measures to identify vulnerabilities before attackers exploit them. With AI-powered tools, assessing threats becomes significantly more efficient.' },
    { img: require('../../../assets/images/services/technology/ai-4.webp'), title: 'Password Protection & Authentication', desc: 'AI enhances protection of user accounts through advanced authentication methods, detecting suspicious login attempts and preventing unauthorized access in real time.' }
];

const aiStats = [
    { value: '95%', label: 'Threat Detection Rate' },
    { value: '<1min', label: 'Average Response Time' },
    { value: '60%', label: 'Fewer False Positives' },
    { value: '24/7', label: 'Continuous Monitoring' }
];

export default function ArtificialIntelligence() {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            {/* Hero Banner */}
            <section className="tech-hero-banner" style={{ position: 'relative', overflow: 'hidden' }}>
                <div className="tech-hero-overlay"></div>
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center" style={{ minHeight: '85vh' }}>
                        <div className="col-lg-7 col-md-12">
                            <motion.div
                                className="tech-hero-content"
                                initial="hidden"
                                animate="visible"
                                variants={staggerContainer}
                            >
                                <motion.div variants={fadeUp} className="tech-breadcrumb">
                                    <Link to="/">Home</Link>
                                    <span className="tech-breadcrumb-sep"> / </span>
                                    <span>Artificial Intelligence</span>
                                </motion.div>
                                <motion.div variants={fadeUp} className="tech-badge">
                                    AI-Powered Cybersecurity
                                </motion.div>
                                <motion.h1 variants={fadeUp} className="tech-hero-title">
                                    Artificial Intelligence<br />
                                    <span className="tech-hero-gradient">in Cybersecurity</span>
                                </motion.h1>
                                <motion.p variants={fadeUp} className="tech-hero-desc">
                                    AI technology is transforming how we combat cybercrime - from speech recognition to fraud prevention. It both enhances security and introduces new vectors that require advanced countermeasures.
                                </motion.p>
                                <motion.div variants={fadeUp} className="tech-hero-actions">
                                    <Link to="/contact" className="tech-btn-primary">Get AI Protection</Link>
                                    <Link to="/contact" className="tech-btn-ghost">View Solutions</Link>
                                </motion.div>
                            </motion.div>
                        </div>
                        <div className="col-lg-5 col-md-12">
                            <motion.div
                                className="tech-hero-visual"
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.9, delay: 0.3 }}
                            >
                                <div className="ai-neural-visual">
                                    <div className="ai-neural-node ai-node-center">AI</div>
                                    {['ML', 'NLP', 'CV', 'DL', 'RL'].map((label, i) => (
                                        <div key={i} className={`ai-neural-node ai-node-${i + 1}`}>
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                    <svg className="ai-neural-lines" viewBox="0 0 220 220">
                                        <circle cx="110" cy="110" r="80" stroke="rgba(36,88,243,0.2)" strokeWidth="1" fill="none" strokeDasharray="4,6" />
                                        <circle cx="110" cy="110" r="50" stroke="rgba(74,165,249,0.15)" strokeWidth="1" fill="none" />
                                    </svg>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <video autoPlay loop muted style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
                    <source src={HomeVideo2} type="video/mp4" />
                </video>
            </section>

            {/* AI Scope Section */}
            <section className="tech-section-light">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag">AI in Security</div>
                            <h2 className="tech-section-heading">Scope of AI in <span className="tech-text-accent">Cybersecurity</span></h2>
                            <p className="tech-section-para">
                                On one hand, AI in cybersecurity improves how security experts analyze, study, and understand cybercrime - enhancing the technologies companies use to combat cybercriminals.
                            </p>
                            <p className="tech-section-para">
                                On the other hand, AI can also serve as a new weapon for cybercriminals. Through machine learning, security platforms can counter these AI-driven threats while protecting data privacy.
                            </p>
                            <div className="tech-dual-cards">
                                <div className="tech-dual-card positive">
                                    <span className="tech-dual-icon">✅</span>
                                    <div>
                                        <h4>AI for Defense</h4>
                                        <p>Enhanced detection, faster response, predictive threat intelligence</p>
                                    </div>
                                </div>
                                <div className="tech-dual-card negative">
                                    <span className="tech-dual-icon">⚠️</span>
                                    <div>
                                        <h4>AI as Threat</h4>
                                        <p>Sophisticated attacks, deepfakes, AI-powered malware</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/compnay/hiring.webp')} alt='AI Cybersecurity' />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Top Benefits */}
            <section className="tech-section-dark">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag light">Key Advantages</div>
                        <h2 className="tech-section-heading text-white">Top Benefits of AI in <span className="tech-text-accent">Cybersecurity</span></h2>
                    </motion.div>
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {benefits.map((b, i) => (
                            <motion.div className="col-lg-4 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-benefit-card">
                                    <div className="tech-benefit-icon">{b.icon}</div>
                                    <h3 className="tech-benefit-title">{b.title}</h3>
                                    <p className="tech-benefit-desc">{b.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Stats */}
            <section className="tech-stats-section">
                <div className="container">
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {aiStats.map((s, i) => (
                            <motion.div className="col-lg-3 col-md-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-stat-card">
                                    <div className="tech-stat-num">{s.value}</div>
                                    <div className="tech-stat-label">{s.label}</div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Capabilities */}
            <section className="tech-section-light">
                <div className="container">
                    <motion.div
                        className="tech-section-header text-center mb-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    >
                        <div className="tech-section-tag">Core Capabilities</div>
                        <h2 className="tech-section-heading">AI <span className="tech-text-accent">Capabilities</span> in Action</h2>
                    </motion.div>
                    <motion.div
                        className="row"
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
                    >
                        {capabilities.map((cap, i) => (
                            <motion.div className="col-lg-6 mb-4" key={i} variants={fadeUp}>
                                <div className="tech-capability-card">
                                    <div className="tech-capability-img">
                                        <img src={cap.img} alt={cap.title} />
                                    </div>
                                    <div className="tech-capability-content">
                                        <h3>{cap.title}</h3>
                                        <p>{cap.desc}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Better Security Section */}
            <section className="tech-section-gradient">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6"
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        >
                            <div className="tech-section-tag light">Complete Protection</div>
                            <h2 className="tech-section-heading text-white">Better Overall <span className="tech-text-accent">Security</span></h2>
                            <p className="tech-section-para text-white" style={{ opacity: 0.9 }}>
                                Hackers change tactics every day. You may face phishing, denial-of-service, and ransomware simultaneously. AI's self-learning capabilities make it equipped to handle multiple threats together - prioritizing and preventing them before damage is done.
                            </p>
                            <ul className="tech-benefits-list">
                                {['Better Overall Security', 'Accelerated Detection Response Time', 'Reduced Duplicate Processes', 'Secured Authentication', '2X Higher Conversion Rates on Platforms'].map((item, i) => (
                                    <li key={i}><span className="tech-check-icon">✓</span>{item}</li>
                                ))}
                            </ul>
                            <Link to="/contact" className="tech-btn-primary mt-4 d-inline-block">Get Started</Link>
                        </motion.div>
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                        >
                            <div className="tech-image-frame">
                                <img src={require('../../../assets/images/services/technology/ai-benefit.jpg')} alt='AI Benefits' />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <FooterContact />
        </>
    )
}
